import { useQuery } from 'apollo-client-ppc';
import { capitalize, defaultTo, memoize, toUpper, get } from 'lodash';
import React, { useCallback, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Select, { ValueType } from '@atlaskit/select';
import {
  FetchInvoicesForAccountBalanceQuery,
  FetchInvoicesForAccountBalanceQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  SoldToAddressDetailsLink,
  errorStateProp,
  emptyStateProp,
  NoData,
  BillingProfileNameFromId,
  Currency,
  getFormattedAmountByCurrency,
  FormatCurrency,
  emptyStatePropForAccountBalanceTable,
} from '@css-xp/ppc-common';
import {
  CustomFilterCheckboxWrapper,
  CustomSearch,
  DynamicResultTable,
  FormattedAddress,
  FormattedDate,
  getFormattedDateString,
} from '@css-xp/base';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { routePaths } from '@css-xp/ppc-constants';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { Text, Box } from '@atlaskit/primitives';
import { DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { transformFilterableFields } from '@atlassian/cssxp-utils-generic';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/cssxp-client-utils';
import { SortDirection } from '@css-xp/ppc-graphtypes';
import { TableLoader } from '@css-xp/ppc-page-loaders';
import { fetchBillingProfileNameById } from '@css-xp/ppc-common/src/queries/fetch-billing-profile-name-by-id';
import { fetchInvoicesForAccountBalance } from './queries/fetch-invoices-for-account-balance';
import './account-balance-table.scss';

export interface AccountBalanceTableProps {
  transactionAccountId: string;
  label: string;
  accountBalanceRecords: any[];
  invoiceNumbers: string[];
  version?: number;
  defaultCurrency?: string;
  isMultiCurrencyChangesEnabled?: boolean;
  setAccountBalanceCurrency: (currency: string) => void;
  accountBalanceDataLoading?: boolean;
}

enum TypeFilterOptions {
  CREDIT = 'Credit',
  DEBIT = 'Debit',
}

enum SortKeys {
  DATE = 'date',
  AMOUNT = 'amount',
}

export interface SelectItem {
  label: string;
  value: string;
}

export const currencySelectOptions = () => {
  const options: SelectItem[] = [];
  for (const [value, label] of Object.entries(Currency)) {
    options.push({ label: toUpper(label), value });
  }

  return options;
};

const initialSelectedFieldsForType: string[] = [];

const initialTypeFilterFields = [
  {
    filterItem: 'Type',
    queryKey: 'type',
    iterableValues: transformFilterableFields(TypeFilterOptions),
    defaultSelectedFields: initialSelectedFieldsForType,
  },
];

const descriptionMapping: Record<string, string> = {
  credit_note: 'Credit - SLA Credit',
  invoice_too_small: 'Credit - Negative invoice',
  applied_to_invoice: 'Debit - Applied to invoice',
};

const typeMapping: Record<string, string[]> = {
  Credit: ['credit_note', 'invoice_too_small'],
  Debit: ['applied_to_invoice'],
};

const fetchPolicy = 'cache-and-network';

interface FormData {
  searchTerm: string;
}

const ansClient = abgGraphqlClient(ProxyGraphClients.ANS);

const getBillingProfileName = async (id: string, txaId: string) => {
  const { data } = await ansClient.query({
    query: fetchBillingProfileNameById,
    variables: {
      id: id ?? '',
      txaId,
    },
  });

  return data?.invoiceGroup;
};

export const AccountBalanceTable = ({
  transactionAccountId,
  label,
  invoiceNumbers,
  accountBalanceRecords,
  version,
  defaultCurrency = Currency.USD,
  isMultiCurrencyChangesEnabled,
  setAccountBalanceCurrency,
  accountBalanceDataLoading = false,
}: AccountBalanceTableProps) => {
  const isVersionFour = version === 4; // Design Iteration
  const [accountBalanceData, setAccountBalanceData] = useState(accountBalanceRecords);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [sortOrder, setSortOrder] = React.useState<SortDirection>(SortDirection.DESC);
  const [sortKey, setSortKey] = React.useState<string>(SortKeys.DATE);
  const [billingProfileNameMap, setBillingProfileNameMap] = useState<any>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<ValueType<SelectItem, false>>({
    value: defaultCurrency,
    label: Currency[defaultCurrency as keyof typeof Currency],
  });

  const {
    data: fetchInvoicesForAccountBalanceResponse,
    loading: fetchInvoicesForAccountBalanceLoading,
    error: fetchInvoicesForAccountBalanceError,
  } = useQuery<FetchInvoicesForAccountBalanceQuery, FetchInvoicesForAccountBalanceQueryVariables>(
    fetchInvoicesForAccountBalance,
    {
      variables: {
        where: {
          and: [{ transactionAccountId: transactionAccountId }, { invoiceNumberIsIn: invoiceNumbers }],
        },
        size: invoiceNumbers.length,
      },
      skip: !transactionAccountId || invoiceNumbers?.length === 0,
      fetchPolicy: fetchPolicy,
      nextFetchPolicy: fetchPolicy,
    }
  );

  useEffect(() => {
    if (version === 4) {
      Promise.allSettled(
        accountBalanceRecords.map(async (record) => {
          if (record.invoiceNumber) {
            const data = await getBillingProfileName(
              extractBillingGroupId(record?.invoiceNumber),
              transactionAccountId
            );
            return { [record.invoiceNumber]: data };
          }
        })
      ).then((data) => {
        if (data) {
          setBillingProfileNameMap(
            data.reduce((init, billingProfile: any) => {
              return { ...init, ...billingProfile?.value };
            }, {})
          );
        }
      });
    }
  }, []);

  const getInvoiceNumberWithLink = (invoiceNumber: string) => {
    return (
      <Link
        to={getRouteUrl(routePaths.invoiceDetails, {
          invoiceId: invoiceNumber,
          txaId: transactionAccountId,
        })}
        data-testid="invoice-link"
      >
        <>{invoiceNumber}</>
      </Link>
    );
  };

  const invoiceNumberToSoldToMap = defaultTo(
    new Map(
      fetchInvoicesForAccountBalanceResponse?.accountInvoices?.results?.map((invoiceData) => [
        invoiceData?.invoiceNumber,
        invoiceData?.shipTo,
      ])
    ),
    new Map()
  );

  const invoiceNumberToBillingGroupId = defaultTo(
    new Map(
      fetchInvoicesForAccountBalanceResponse?.accountInvoices?.results?.map((invoiceData) => [
        invoiceData?.invoiceNumber,
        { billingGroupId: invoiceData?.billingGroupId },
      ])
    ),
    new Map()
  );

  const extractSoldToAddress = (invoiceNumber: string) => {
    return defaultTo(invoiceNumberToSoldToMap?.get(invoiceNumber), null);
  };

  const extractBillingGroupId = (invoiceNumber: string) => {
    return defaultTo(invoiceNumberToBillingGroupId?.get(invoiceNumber)?.billingGroupId, null);
  };

  const getSoldToAddressInfo = (invoiceNumber: string) => {
    const soldToAddress = extractSoldToAddress(invoiceNumber);

    if (soldToAddress && soldToAddress?.address) {
      return (
        <SoldToAddressDetailsLink
          txaId={transactionAccountId}
          shipToId={soldToAddress.id}
          shouldDisableLink={isVersionFour}
        >
          <div className="line-clamp">
            <FormattedAddress
              addressLine1={soldToAddress?.address?.address1}
              addressLine2={soldToAddress?.address?.address2}
              city={soldToAddress?.address?.city}
              country={soldToAddress?.address?.country}
              postcode={soldToAddress?.address?.postalCode}
              state={soldToAddress?.address?.state}
            />
          </div>
        </SoldToAddressDetailsLink>
      );
    }
    return <NoData />;
  };

  const getCompanyName = (invoiceNumber: string) => {
    const soldToAddress = extractSoldToAddress(invoiceNumber);
    if (soldToAddress && soldToAddress?.fullName) {
      return (
        <SoldToAddressDetailsLink txaId={transactionAccountId} companyName={soldToAddress.fullName}>
          <>{soldToAddress.fullName}</>
        </SoldToAddressDetailsLink>
      );
    }
    return <NoData />;
  };

  const prettifyAmount = (amount: number, currency: string) => {
    return (
      <FormatCurrency
        value={getFormattedAmountByCurrency(Math.abs(defaultTo(amount, 0)), currency)}
        currency={currency}
        showCurrencyCode
        showCommas
      />
    );
  };

  const formatAmount = (amount: number, currency: string, showWithSign?: boolean) => {
    return (
      <>
        {showWithSign && (amount < 0 ? '-' : '+')}
        <FormatCurrency
          value={getFormattedAmountByCurrency(Math.abs(defaultTo(amount, 0)), currency)}
          currency={currency}
          showCommas
        />
      </>
    );
  };

  const renderTableCell = (title?: string, subTitle?: string): JSX.Element => {
    return (
      <td>
        <Text>{title}</Text>
        <br />
        <Text color="color.text.subtle" size="small">
          {subTitle}
        </Text>
      </td>
    );
  };

  const getRowsData = useCallback(() => {
    const start = (currentPage - 1) * rowsPerPage;
    const accountBalanceRecords = [...accountBalanceData];
    if (isVersionFour) {
      if (sortKey === SortKeys.DATE) {
        accountBalanceRecords.sort((a, b) => {
          if (sortOrder === SortDirection.ASC) {
            return a.createdAt - b.createdAt;
          }
          return b.createdAt - a.createdAt;
        });
      }

      if (sortKey === SortKeys.AMOUNT) {
        accountBalanceRecords.sort((a, b) => {
          if (sortOrder === SortDirection.ASC) {
            return a.amount - b.amount;
          }
          return b.amount - a.amount;
        });
      }
    }
    const paginatedData = accountBalanceRecords.slice(start, start + rowsPerPage);
    return paginatedData.map((accountBalanceRecord: any) => {
      if (isVersionFour) {
        const [typeTitle, typeSubtitle] = accountBalanceRecord?.type
          ? descriptionMapping[accountBalanceRecord?.type]?.split('-') || accountBalanceRecord?.type?.split('_')
          : [];
        const [date, timeZone] = accountBalanceRecord.createdAt
          ? (getFormattedDateString({
              date: new Date(accountBalanceRecord.createdAt).toISOString(),
              format: DATE_TIME_FORMAT,
            })?.split(',') as string[])
          : [];

        return {
          key: transactionAccountId,
          cells: [
            {
              key: SortKeys.DATE,
              content: accountBalanceRecord?.createdAt ? renderTableCell(date, timeZone) : <NoData />,
            },
            {
              content: accountBalanceRecord?.type ? renderTableCell(typeTitle, typeSubtitle) : <NoData />,
            },
            {
              content: accountBalanceRecord?.invoiceNumber ? (
                <td>{getCompanyName(accountBalanceRecord?.invoiceNumber)}</td>
              ) : (
                <NoData />
              ),
            },
            {
              content: accountBalanceRecord?.invoiceNumber ? (
                getInvoiceNumberWithLink(accountBalanceRecord?.invoiceNumber)
              ) : (
                <NoData />
              ),
            },
            {
              content: accountBalanceRecord?.invoiceNumber ? (
                <BillingProfileNameFromId
                  id={extractBillingGroupId(accountBalanceRecord?.invoiceNumber)}
                  txaId={transactionAccountId || ''}
                  showBillingProfileNumber={true}
                />
              ) : (
                <NoData />
              ),
            },
            {
              content: accountBalanceRecord?.amount ? (
                <td>{formatAmount(accountBalanceRecord?.amount, accountBalanceRecord?.currency, true)}</td>
              ) : (
                <NoData />
              ),
            },
            {
              content:
                accountBalanceRecord?.endingAmount !== undefined && accountBalanceRecord?.endingAmount !== null ? (
                  <td>{formatAmount(accountBalanceRecord?.endingAmount, accountBalanceRecord?.currency)}</td>
                ) : (
                  <NoData />
                ),
            },
          ],
        };
      }

      return {
        key: transactionAccountId,
        cells: [
          {
            content: accountBalanceRecord?.createdAt ? (
              <FormattedDate date={new Date(accountBalanceRecord.createdAt).toISOString()} showTime={true} />
            ) : (
              <NoData />
            ),
          },
          {
            content: accountBalanceRecord?.invoiceNumber ? (
              getInvoiceNumberWithLink(accountBalanceRecord?.invoiceNumber)
            ) : (
              <NoData />
            ),
          },
          {
            content: accountBalanceRecord?.type ? (
              <td>
                {descriptionMapping[accountBalanceRecord?.type] ||
                  capitalize(accountBalanceRecord?.type?.split('_').join(' '))}
              </td>
            ) : (
              <NoData />
            ),
          },
          {
            content: accountBalanceRecord?.invoiceNumber ? (
              <td>{getCompanyName(accountBalanceRecord?.invoiceNumber)}</td>
            ) : (
              <NoData />
            ),
          },
          {
            content: accountBalanceRecord?.invoiceNumber ? (
              <td>{getSoldToAddressInfo(accountBalanceRecord?.invoiceNumber)}</td>
            ) : (
              <NoData />
            ),
          },
          {
            content:
              accountBalanceRecord?.amount < 0 ? (
                <td>{prettifyAmount(accountBalanceRecord?.amount, accountBalanceRecord?.currency)}</td>
              ) : (
                <NoData />
              ),
          },
          {
            content:
              accountBalanceRecord?.amount > 0 ? (
                <td>{prettifyAmount(accountBalanceRecord?.amount, accountBalanceRecord?.currency)}</td>
              ) : (
                <NoData />
              ),
          },
          {
            content:
              accountBalanceRecord?.endingAmount !== undefined && accountBalanceRecord?.endingAmount !== null ? (
                <td>{prettifyAmount(accountBalanceRecord?.endingAmount, accountBalanceRecord?.currency)}</td>
              ) : (
                <NoData />
              ),
          },
        ],
      };
    });
  }, [invoiceNumberToSoldToMap]);

  const getTableColumnNames = memoize(() => {
    if (isVersionFour) {
      const currency = isMultiCurrencyChangesEnabled ? selectedCurrency?.value : Currency.USD;
      const cells = [
        {
          content: 'Date',
          shouldTruncate: true,
          isSortable: true,
          key: SortKeys.DATE,
        },
        {
          content: 'Type',
        },
        {
          content: 'Company',
        },
        {
          content: 'Linked invoice',
        },
        {
          content: 'Billing profile',
          width: 20,
        },
        {
          content: `Amount (${currency})`,
          isSortable: true,
          key: SortKeys.AMOUNT,
        },
        {
          content: `Account balance (${currency})`,
        },
      ];
      return {
        cells,
      };
    }

    const cells = [
      {
        content: 'Date',
        shouldTruncate: true,
      },
      {
        content: 'Linked invoice',
      },
      {
        content: 'Details',
      },
      {
        content: 'Company',
      },
      {
        content: 'Sold-to address',
      },
      {
        content: 'Credit',
      },
      {
        content: 'Debit',
      },
      {
        content: 'Account balance',
      },
    ];
    return {
      cells,
    };
  });

  useEffect(() => {
    setAccountBalanceData([...accountBalanceRecords]);
  }, [accountBalanceRecords]);

  const handleSearch = (formData: FormData) => {
    const input = formData && formData.searchTerm;
    const filteredData = input
      ? accountBalanceRecords.filter((record) => {
          const { name, invoiceGroupNumber } = billingProfileNameMap?.[record.invoiceNumber] || {};
          return (
            record.invoiceNumber === input ||
            invoiceGroupNumber === input ||
            name?.toLowerCase().includes(input.toLowerCase())
          );
        })
      : accountBalanceRecords;
    setAccountBalanceData(filteredData);
  };

  const onPageChange = React.useCallback(async (newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const onRowsPerPageChange = React.useCallback(async (rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const handleTypeFilter = (selectedTypeFilters: { key: string; value: string }[] | Record<string, string>[]) => {
    const filteredData = accountBalanceRecords.filter((record) =>
      selectedTypeFilters.some(({ type }) => typeMapping[type]?.includes(record.type))
    );
    setAccountBalanceData(filteredData.length ? filteredData : accountBalanceRecords);
  };

  const renderTypeFilter = () => {
    return (
      <CustomFilterCheckboxWrapper
        filterFields={initialTypeFilterFields}
        handleFilterUpdate={handleTypeFilter}
        shouldHideClearFilterButton={true}
      />
    );
  };

  const renderCurrencyFilter = () => {
    return (
      <div className="currency-type-filter">
        <Box paddingBlockEnd="space.200" testId="currency-type-filter">
          <Select<SelectItem>
            isSearchable={false}
            options={currencySelectOptions()}
            placeholder="Currency"
            className="select-item"
            data-testid="select-currency-field"
            value={selectedCurrency}
            onChange={(item: ValueType<SelectItem, false>) => {
              setSelectedCurrency(item);
              setAccountBalanceCurrency(defaultTo(get(item, 'value'), Currency.USD));
            }}
            {...(isVersionFour && {
              styles: {
                dropdownIndicator: (base) => ({
                  ...base,
                  paddingTop: 0,
                  paddingBottom: 0,
                }),
                control: (base) => ({
                  ...base,
                  minHeight: 32,
                }),
              },
            })}
          />
        </Box>
      </div>
    );
  };

  const handleSorting = (data: { key: string; sortOrder: SortDirection }) => {
    setSortOrder(data.sortOrder);
    setSortKey(data.key);
  };

  if (isVersionFour && fetchInvoicesForAccountBalanceLoading) {
    return (
      <div className="account-balance-table-loader">
        <TableLoader hideActions columnsCount={7} />
      </div>
    );
  }

  return (
    <DynamicResultTable
      testId="account-balance-table"
      headingText={label}
      head={getTableColumnNames()}
      rows={getRowsData()}
      error={fetchInvoicesForAccountBalanceError?.toString()}
      emptyStateProps={isVersionFour ? emptyStatePropForAccountBalanceTable : emptyStateProp}
      errorStateProps={errorStateProp}
      isLoading={fetchInvoicesForAccountBalanceLoading || accountBalanceDataLoading}
      totalCount={accountBalanceData.length}
      showFilters={isVersionFour || isMultiCurrencyChangesEnabled}
      isPaginationEnabled={isVersionFour && accountBalanceData.length > DEFAULT_ROWS_PER_PAGE}
      rowsPerPage={rowsPerPage}
      currentPage={currentPage}
      onPageChange={onPageChange}
      onRowChange={onRowsPerPageChange}
      sortKey={isVersionFour ? sortKey : undefined}
      sortOrder={isVersionFour ? sortOrder : undefined}
      onSort={isVersionFour ? handleSorting : undefined}
    >
      {isVersionFour && (
        <div className="account-table-filter-container">
          <CustomSearch
            placeholder="Search transactions"
            onSearch={handleSearch}
            isClearable={true}
            helperMessage="Enter company, IN-# or billing profile"
          />
          {renderTypeFilter()}
          {isMultiCurrencyChangesEnabled && renderCurrencyFilter()}
        </div>
      )}
      {version !== 4 && isMultiCurrencyChangesEnabled && (
        <div className="account-table-filter-container">{renderCurrencyFilter()}</div>
      )}
    </DynamicResultTable>
  );
};
